/* HOYA city block in the cart drawer (snippets/hoya-delivery-hint.liquid): the shopper types a city and sees this
   basket's delivery price there, with "שינוי עיר"; a spelling the calculator does not know gets "האם התכוונתם ל…".
   Where there is no price to show (a city outside the delivery zones, a spelling the calculator does not know, the
   calculator unreachable) the block says the price comes at the next step, and the drawer pays as usual: checkout
   prices the full address. No negative words before checkout (owner, 2026-10-19).
   Prices come from Deco Garden's calculator through assets/hoya-zone-terms.js; suggestions come from
   assets/hoya-city-match.js and are offered only once the calculator recognises them. Served and closed cities are
   remembered in this browser (localStorage 'hoya:delivery:v1'). The drawer re-renders on every cart change: the new
   element starts again from the remembered city, priced for the new basket.
   What the block shows is announced with the bubbling `hoya:city-change` event, detail
   { state: 'none' | 'served' | 'closed', city, feeCents, canonicalCity } and kept in `cityState` (null until the first
   announcement) for an element that connects after this one. `city` is what the block shows (as typed, or the official
   name after a suggestion); `feeCents` is this basket's fee in agorot (null unless served); `canonicalCity` is the
   calculator's official name for a served city (the typed city when it sends none, '' otherwise), the one checkout
   gets, as from the cart page. */
(() => {
  if (customElements.get('hoya-delivery-hint')) return;
  const { cityKey, fill, tiers, tierText, lookup, formatMoney, remembered, remember, track } = window.HoyaZoneTerms;
  const NBSP = '\u00a0';
  const LOOK_TIMEOUT = 5000;
  const none = () => ({ state: 'none', city: '', feeCents: null, canonicalCity: '' });
  const known = result => result?.state === 'served' || result?.state === 'closed';

  class HoyaDeliveryHint extends HTMLElement {
    connectedCallback() {
      this.t = JSON.parse(this.querySelector('[data-hint-translations]').textContent);
      this.basis = Number(this.dataset.basis) || 0;
      this.source = this.dataset.zonesSource;
      this.citiesUrl = this.dataset.citiesUrl;
      this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
      this.ask = this.querySelector('[data-hint-ask]');
      this.input = this.querySelector('[data-hint-input]');
      this.checkButton = this.querySelector('[data-hint-check]');
      this.message = this.querySelector('[data-hint-message]');
      this.suggestionList = this.querySelector('[data-hint-suggestions]');
      this.resultBox = this.querySelector('[data-hint-result]');
      this.line = this.querySelector('[data-hint-line]');
      this.changeButton = this.querySelector('[data-hint-change]');
      this.bar = this.querySelector('[data-hint-bar]');
      this.tier = this.querySelector('[data-hint-tier]');
      this.live = this.querySelector('[data-hint-live]');
      this.suggestions = [];
      this.shown = null;
      this.cityState = null;
      // Every check takes the next number, and only the newest may change the block. The count survives a reconnect,
      // so an answer started before it can never pass for a new one.
      this.checks = (this.checks || 0) + 1;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      // The drawer's way on: its button to the cart page and the pickup link (sections/cart-drawer.liquid).
      document.addEventListener('click', event => {
        if (event.target?.closest?.('cart-drawer a[href$="/cart"], cart-drawer a[href*="/cart?"]')) track('hoya_drawer_continue', { subtotal: this.basis });
      }, options);
      this.addEventListener('click', event => {
        const target = event.target;
        if (!target?.closest) return;
        if (target.closest('[data-hint-check]')) this.check();
        else if (target.closest('[data-hint-change]')) this.change();
        else if (target.closest('[data-hint-suggestion]')) this.pick(Number(target.closest('[data-hint-suggestion]').dataset.hintSuggestion));
      }, options);
      // Enter checks the city. The drawer footer is a form posting to /cart, which Enter must never submit.
      this.addEventListener('keydown', event => {
        if (event.key !== 'Enter' || event.target !== this.input || event.isComposing) return;
        event.preventDefault();
        this.check();
      }, options);
      this.open();
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    // The remembered city, priced for this basket (its answer is normally cached). No remembered city: the empty field
    // and no calculator call. Opening is not a check: nothing is announced or counted.
    async open() {
      const sequence = this.checks;
      const signal = this.controller.signal;
      const city = cityKey(remembered()?.city);
      if (city.length < 2) {
        this.showField('');
        this.emit(none());
        return;
      }
      this.showField(city);
      this.setBusy(true);
      const result = await this.look(city, signal);
      if (!result || this.stale(sequence, signal)) return;
      this.setBusy(false);
      if (known(result)) {
        this.emit(this.showResult(city, result));
        return;
      }
      // Not recognised any more, or the calculator is unreachable: the city stays in the field, priced at the next step.
      this.showField(city, { message: fill(this.t.next_step, { city }) });
      this.emit(none());
    }

    // Enter or "בדיקה". A served or closed city is remembered and shown (states 2 and 3); an unknown one gets the
    // suggestions the calculator recognises (state 4); without any, or with the calculator unreachable, the field keeps
    // the city and says its price comes at the next step (state 5). The previous message and suggestions stay until the
    // new answer replaces them.
    async check() {
      const city = cityKey(this.input.value);
      if (city.length < 2) {
        this.input.focus();
        return;
      }
      const sequence = ++this.checks;
      const signal = this.controller.signal;
      const owned = this.contains(document.activeElement);
      this.setBusy(true);
      this.announce(this.t.checking);
      const result = await this.look(city, signal);
      if (!result || this.stale(sequence, signal)) return;
      // Ranking and verifying suggestions can take a while (the city list, then up to three lookups): still busy.
      const found = result.state === 'unknown' ? await this.suggest(city, signal) : [];
      if (this.stale(sequence, signal)) return;
      this.setBusy(false);
      if (known(result)) {
        this.keep(city);
        this.emit(this.showResult(city, result));
        this.settleFocus(owned, this.changeButton);
      } else {
        this.showField(null, { message: fill(this.t.next_step, { city }), suggestions: found });
        this.emit(none());
        // A suggestion is the likely next tap: focus moves to it, which on a phone also closes the keyboard that would
        // hide it. Without one, focus stays in the field for another try.
        const suggestion = this.suggestionList.querySelector('[data-hint-suggestion]');
        if (suggestion) this.settleFocus(owned, suggestion, true);
        else this.settleFocus(owned, this.checkButton);
      }
      track('hoya_drawer_city', { state: result.state, suggested: found.length > 0 });
      this.announce(this.shownText());
    }

    // A suggestion shows its price at once, from the answer already fetched, and wins over a check still running.
    pick(index) {
      const choice = this.suggestions[index];
      if (!choice) return;
      ++this.checks;
      this.setBusy(false);
      this.input.value = choice.name;
      this.keep(choice.name);
      this.emit(this.showResult(choice.name, choice.result));
      this.settleFocus(true, this.changeButton);
      track('hoya_city_suggestion', { where: 'drawer' });
      this.announce(this.shownText());
    }

    // "שינוי עיר": the field again, holding the current city, focused and selected. No price shows meanwhile.
    change() {
      const city = this.shown?.city ?? '';
      this.showField(city);
      this.emit(none());
      this.input.focus();
      this.input.select();
    }

    // One calculator answer for a city; null once this block was removed (its requests are aborted then). 5 s a try
    // (two tries), not the lookup's 9 s: an unreachable calculator says "try again" within about 10 s.
    async look(city, signal) {
      try {
        return await lookup(city, { source: this.source, signal, timeout: LOOK_TIMEOUT });
      } catch (_) {
        return null;
      }
    }

    // Official names the calculator recognises, closest to the typed one. Without the list, the matcher or the
    // calculator there are none, and the shopper is never blocked.
    async suggest(city, signal) {
      const matcher = window.HoyaCityMatch;
      if (!matcher?.suggestVerified || !this.citiesUrl) return [];
      try {
        const found = await matcher.suggestVerified(city, { citiesUrl: this.citiesUrl, lookup, source: this.source, signal });
        // Never "did you mean" the very text that was typed.
        return found.filter(({ name, result }) => cityKey(name) !== city && known(result));
      } catch (_) {
        return [];
      }
    }

    stale(sequence, signal) {
      return signal.aborted || sequence !== this.checks;
    }

    // Served and closed cities are remembered in this browser. The delivery choice stays as it was; a street,
    // apartment and zip belong to their city, so a new city clears them.
    keep(city) {
      const stored = remembered();
      const same = Boolean(stored) && cityKey(stored.city) === cityKey(city);
      remember({
        mode: stored?.mode === 'pickup' ? 'pickup' : 'shipping',
        city,
        address1: same ? stored.address1 : '',
        address2: same ? stored.address2 : '',
        zip: same ? stored.zip : ''
      });
    }

    // States 1, 4 and 5: the field (a null city keeps what is typed), with a message and suggestions under it.
    showField(city, options = {}) {
      this.shown = null;
      if (city !== null) this.input.value = city;
      this.resultBox.hidden = true;
      this.ask.hidden = false;
      this.say(options);
    }

    // States 2 and 3: this basket's price there with the bar and the next tier, or, outside the delivery zones, that the
    // price comes at the next step. Returns what the drawer is told.
    showResult(city, result) {
      this.shown = { city, result };
      this.say();
      this.ask.hidden = true;
      this.resultBox.hidden = false;
      if (result.state === 'closed') {
        this.line.textContent = fill(this.t.next_step, { city });
        this.bar.hidden = true;
        this.tier.textContent = '';
        this.tier.hidden = true;
        return { state: 'closed', city, feeCents: null, canonicalCity: '' };
      }
      const step = tiers(result.terms, this.basis);
      const tier = tierText(step, this.t, cents => this.price(cents));
      this.line.textContent = fill(this.t.fee, { city, amount: this.price(step.fee) });
      this.bar.hidden = !(tier && step.fee > 0);
      this.bar.firstElementChild.style.inlineSize = `${Math.max(4, Math.min(100, Math.round(step.ratio * 100)))}%`;
      this.tier.textContent = tier;
      this.tier.hidden = !tier;
      return { state: 'served', city, feeCents: step.fee, canonicalCity: cityKey(result.canonicalCity) || city };
    }

    // The message under the field and the suggestion buttons.
    say({ message = '', suggestions = [] } = {}) {
      this.message.textContent = message;
      this.message.hidden = !message;
      this.suggestions = suggestions;
      this.suggestionList.replaceChildren(...suggestions.map(({ name }, index) => this.suggestionButton(name, index)));
      this.suggestionList.hidden = !suggestions.length;
    }

    // "האם התכוונתם ל־<strong>פתח תקווה</strong>?": bold only through <strong> (icube.css forces normal weight).
    suggestionButton(name, index) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'hoya-hint__suggest';
      button.dataset.hintSuggestion = String(index);
      const strong = document.createElement('strong');
      strong.textContent = name;
      const [before, ...after] = String(this.t.suggest).split('{city}');
      button.append(before, strong, after.join(name));
      return button;
    }

    // The store's money format, glued to its ₪ with a no-break space: a wrapped line never ends in "49" above "₪".
    price(cents) {
      return cents === 0 ? this.t.free : formatMoney(cents, this.moneyFormat).replace(/ /g, NBSP);
    }

    // While a city is checked "בדיקה" is disabled with a busy look; the field stays open for a newer city.
    setBusy(busy) {
      this.checkButton.disabled = busy;
      if (busy) this.checkButton.setAttribute('aria-busy', 'true');
      else this.checkButton.removeAttribute('aria-busy');
    }

    // After an answer to a check started from this block (`owned`), focus moves to `target` when it is lost: left on a
    // control that is now hidden or gone, or dropped while "בדיקה" was disabled. With `take`, focus anywhere in this
    // block moves too. Focus the shopper moved elsewhere stays where it is.
    settleFocus(owned, target, take = false) {
      if (!owned) return;
      const active = document.activeElement;
      const lost = !active || active === document.body || !active.isConnected || (this.ask.hidden && this.ask.contains(active));
      if (lost || (take && this.contains(active))) target.focus();
    }

    announce(text) {
      this.live.textContent = text;
    }

    // What the block now says, as the live region reads it out: a stop after each part but the last, so the message and
    // a suggestion are read as two sentences.
    shownText() {
      if (!this.resultBox.hidden) return this.line.textContent;
      const message = this.message.hidden ? '' : this.message.textContent;
      const parts = [message, ...[...this.suggestionList.children].map(button => button.textContent)].filter(Boolean);
      return parts.map((part, index) => (index < parts.length - 1 && !/[.!?…]$/.test(part) ? `${part}.` : part)).join(' ');
    }

    // Tells the drawer what the block shows (its total row and pay button follow it); a repeat of the same is skipped.
    emit(detail) {
      const last = this.cityState;
      if (last && ['state', 'city', 'feeCents', 'canonicalCity'].every(key => last[key] === detail[key])) return;
      this.cityState = Object.freeze({ ...detail });
      this.dispatchEvent(new CustomEvent('hoya:city-change', { bubbles: true, detail: { ...detail } }));
    }
  }

  customElements.define('hoya-delivery-hint', HoyaDeliveryHint);
})();
